import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { InvoiceCompareBar } from '@/components/InvoiceCompareBar'
import {
  INVOICE_COMPARE_FIELDS,
  differingInvoiceFields,
  type CustomerInvoiceValues,
} from '@/lib/invoiceCustomerDiff'
import { serviceDetailPaths } from '@/lib/relinkedServiceChange'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import { CalloutNote } from '@/components/CalloutNote'
import { CalloutPhotoNote } from '@/components/CalloutPhotoNote'
import {
  isAuthoredNote,
  type CalloutNotesSectionNote,
} from '@/components/CalloutNotesSection'
import { AddendumsCard } from '@/components/AddendumsCard'
import { InvoiceStatusChangesGroup } from '@/components/InvoiceStatusChangesGroup'
import { LinkedCustomer } from '@/components/LinkedCustomer'
import { LockedStatusCard } from '@/components/LockedStatusCard'
import { buildAddendumChanges } from '@/lib/addendumFields'
import type { Addendum, AddendumChange, ServiceStatus, StatusChangeLog } from '@/lib/schemas/db'

export type BillingOutcome = 'OmniInvoice' | 'NoOmniInvoice' | 'PosPayment'

export interface InvoiceServiceScreenProps {
  serviceType: 'Callout' | 'Session' | 'SitDown' | 'Job'
  resourceLabel?: string
  publicId?: string
  customerName: string
  customerId?: string
  customerPublicId?: string
  visitAddress?: string
  siteContact?: string
  contactPhone?: string
  contactCell?: string
  contactEmail?: string
  customerAddress?: string
  invoiceTo?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceAddress?: string
  invoiceVat?: string
  /**
   * The service's own invoice values and the customer's current ones, for the
   * "does this still match the customer?" check. Omit either to skip it.
   */
  serviceInvoice?: CustomerInvoiceValues
  customerInvoice?: CustomerInvoiceValues
  issueSummary: string
  scheduledAt?: string
  calloutStatus?: ServiceStatus
  statusChanges?: StatusChangeLog[]
  getAuthorDisplayName?: (authorId: string) => string
  omniCustomerId?: string
  omniInvoiceId?: string
  billingOutcome?: BillingOutcome
  notes: CalloutNotesSectionNote[]
  onMarkInvoiced?: (params: {
    omniInvoiceId?: string
    noOmniInvoice: boolean
    billingOutcome?: BillingOutcome
  }) => void | Promise<void>
  onMoveToReview?: (reason: string) => void | Promise<void>
  lockedStatus?: 'Invoiced' | 'Cancelled'
  lockedActionLabel?: string
  lockedActionTitle?: string
  lockedActionDescription?: string
  lockedMessage?: string
  lockedReasonLabel?: string
  lockedReasonPlaceholder?: string
  lockedConfirmLabel?: string
  lockedCancelLabel?: string
  invoiceReadyStatusLabel?: string
  onMoveToBooked?: () => void | Promise<void>
  showHeader?: boolean
  /** Amendments already recorded on this locked record, oldest first. */
  addendums?: Addendum[]
  /**
   * Record an amendment (currently the Omni invoice ID) on a locked record.
   * With `lockedStatus`, enables the amend flow in place of the plain
   * locked-status card.
   */
  onSaveAddendum?: (
    reason: string,
    changes: AddendumChange[],
  ) => void | Promise<void>
}

function InvoiceCompareOnPrep(props: {
  service: CustomerInvoiceValues
  customer: CustomerInvoiceValues
  detailsLink?: { serviceType: 'Callout' | 'Session' | 'Job'; id: string }
}) {
  const { service, customer, detailsLink } = props
  const [expanded, setExpanded] = useState(false)
  const differing = differingInvoiceFields(service, customer)
  return (
    <div className="space-y-2">
      <InvoiceCompareBar
        differing={differing.length}
        total={INVOICE_COMPARE_FIELDS.length}
        expanded={expanded}
        onToggle={() => setExpanded((open) => !open)}
      >
        {differing.length > 0 && detailsLink ? (
          <Link
            to={serviceDetailPaths[detailsLink.serviceType]}
            params={{ id: detailsLink.id }}
            className="rounded border border-amber-300 bg-white px-2 py-0.5 font-medium hover:bg-amber-100"
          >
            Review on Details
          </Link>
        ) : null}
      </InvoiceCompareBar>
      {expanded && differing.length > 0 ? (
        <dl
          data-testid="invoice-prep-diffs"
          className="space-y-1 rounded border border-amber-200 bg-white px-3 py-2 text-xs"
        >
          {differing.map((field) => (
            <div key={field.key} className="grid grid-cols-[8rem_1fr_1fr] gap-2">
              <dt className="text-slate-500">{field.label}</dt>
              <dd className="whitespace-pre-line">
                <span className="text-slate-400">On this record: </span>
                {service[field.key]?.trim() || '(empty)'}
              </dd>
              <dd className="whitespace-pre-line">
                <span className="text-slate-400">Customer: </span>
                <strong>{customer[field.key]?.trim()}</strong>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  )
}

export function InvoiceServiceScreen({
  serviceType,
  resourceLabel = 'Callout',
  publicId,
  customerName,
  customerId,
  visitAddress,
  siteContact,
  contactPhone,
  contactCell,
  contactEmail,
  customerAddress,
  invoiceTo,
  invoiceEmail,
  invoicePhone,
  invoiceAddress,
  invoiceVat,
  serviceInvoice,
  customerInvoice,
  omniCustomerId,
  omniInvoiceId,
  billingOutcome,
  calloutStatus,
  statusChanges = [],
  getAuthorDisplayName,
  addendums = [],
  onSaveAddendum,
  notes,
  onMarkInvoiced,
  onMoveToReview,
  onMoveToBooked,
  lockedStatus,
  lockedActionLabel,
  lockedActionTitle,
  lockedActionDescription,
  lockedMessage,
  lockedReasonLabel,
  lockedReasonPlaceholder,
  lockedConfirmLabel,
  lockedCancelLabel,
  invoiceReadyStatusLabel,
  showHeader = true,
}: InvoiceServiceScreenProps) {
  const [checkedNotes, setCheckedNotes] = useState<Record<string, boolean>>({})
  const [statusChangesHandled, setStatusChangesHandled] = useState(false)
  const [omniInvoiceIdInput, setOmniInvoiceIdInput] = useState(
    omniInvoiceId ?? '',
  )
  const isJob = resourceLabel === 'Job'
  const detailsServiceType: 'Callout' | 'Session' | 'Job' = isJob
    ? 'Job'
    : serviceType === 'Callout'
      ? 'Callout'
      : 'Session'
  const [selectedBillingOutcome, setSelectedBillingOutcome] = useState<BillingOutcome>(
    billingOutcome ?? 'OmniInvoice',
  )
  const [isMarkingInvoiced, setIsMarkingInvoiced] = useState(false)
  const [amending, setAmending] = useState(false)
  const [amendInvoiceIdInput, setAmendInvoiceIdInput] = useState('')
  const [addendumSaveError, setAddendumSaveError] = useState<string | null>(null)

  // Only the Omni invoice ID is amendable here — correcting it after
  // invoicing is the common case. Everything else amends from the Details tab.
  const pendingAddendumChanges = amending
    ? buildAddendumChanges(
        { invoiceId: omniInvoiceId ?? '' },
        { invoiceId: amendInvoiceIdInput },
      )
    : []

  // Who locked the record, and when — the latest transition into the locked
  // status.
  const lockStatusChange = lockedStatus
    ? [...statusChanges].reverse().find((change) => change.newStatus === lockedStatus)
    : undefined

  const resolveAuthorName = (authorId: string) =>
    getAuthorDisplayName?.(authorId) ?? authorId

  const handleSaveAddendum = async (
    reason: string,
    changes: AddendumChange[],
  ) => {
    if (!onSaveAddendum) return
    setAddendumSaveError(null)
    try {
      await onSaveAddendum(reason, changes)
      setAmending(false)
    } catch (error) {
      setAddendumSaveError(
        error instanceof Error ? error.message : 'Could not save the addendum.',
      )
      throw error
    }
  }

  const nonStatusNotes = notes.filter(isAuthoredNote)
  const groupedStatusChanges = notes
    .filter((note) => note.kind === 'status_change')
    .map((note) => ({
      id: note.id,
      createdAt: note.createdAt,
      previousStatus: note.previousStatus,
      newStatus: note.newStatus,
      authorName: note.authorName,
      reason: note.reason,
    }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  const hasNotes = notes.length > 0
  const allNonStatusNotesHandled =
    nonStatusNotes.length === 0 || nonStatusNotes.every((note) => checkedNotes[note.id])
  const allStatusChangesHandled =
    groupedStatusChanges.length === 0 || statusChangesHandled
  const allNotesHandled =
    !hasNotes || (allNonStatusNotesHandled && allStatusChangesHandled)

  const hasInvoiceId = omniInvoiceIdInput.trim().length > 0
  const noOmniInvoice = selectedBillingOutcome === 'NoOmniInvoice'
  const isPosPayment = selectedBillingOutcome === 'PosPayment'
  const readyStatus = invoiceReadyStatusLabel ?? 'ReadyToInvoice'
  const isReadyToInvoice = calloutStatus === readyStatus
  const hasValidJobBillingSelection =
    selectedBillingOutcome !== 'OmniInvoice' || hasInvoiceId
  const canMarkInvoiced =
    isReadyToInvoice &&
    (isJob
      ? hasValidJobBillingSelection
      : allNotesHandled && (isPosPayment || hasInvoiceId || noOmniInvoice))

  const markInvoicedDisabledReason = !isReadyToInvoice
    ? `${resourceLabel} must be ${readyStatus.replace(/([A-Z])/g, ' $1').trim()} before marking as invoiced.`
    : isJob && !hasValidJobBillingSelection
      ? 'Enter an Omni invoice ID when "Omni invoice created" is selected.'
    : !isJob && !allNotesHandled
      ? 'Mark all notes/status changes as handled first.'
      : !isJob && !(isPosPayment || hasInvoiceId || noOmniInvoice)
        ? isJob
          ? 'Choose a billing outcome, and enter an Omni invoice ID if one was created.'
          : `Enter an Omni invoice ID or confirm no Omni invoice for this ${resourceLabel}.`
        : null

  const toggleNoteHandled = (id: string) => {
    setCheckedNotes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const handleMarkInvoicedClick = async () => {
    if (!canMarkInvoiced || !onMarkInvoiced || isMarkingInvoiced) return

    const trimmedInvoiceId = omniInvoiceIdInput.trim()
    setIsMarkingInvoiced(true)
    try {
      await Promise.resolve(
        onMarkInvoiced({
          omniInvoiceId: hasInvoiceId ? trimmedInvoiceId : undefined,
          noOmniInvoice,
          billingOutcome: isJob ? selectedBillingOutcome : undefined,
        }),
      )
    } finally {
      setIsMarkingInvoiced(false)
    }
  }

  const body = (
    <section className="space-y-4">
      {lockedStatus && onSaveAddendum ? (
        <>
          <AddendumsCard
            resourceLabel={resourceLabel}
            status={lockedStatus}
            addendums={addendums}
            getAuthorDisplayName={resolveAuthorName}
            lockedAt={lockStatusChange?.createdAt}
            lockedByName={
              lockStatusChange
                ? resolveAuthorName(lockStatusChange.authorId)
                : undefined
            }
            amending={amending}
            onStartAmend={() => {
              setAddendumSaveError(null)
              setAmendInvoiceIdInput(omniInvoiceId ?? '')
              setAmending(true)
            }}
            onDiscardAmend={() => {
              setAmending(false)
              setAddendumSaveError(null)
            }}
            pendingChanges={pendingAddendumChanges}
            onSaveAddendum={handleSaveAddendum}
            saveError={addendumSaveError}
          />
          {amending ? (
            <div className="rounded-md border border-amber-300 bg-white p-4 space-y-2">
              <FormCapsLabel>Omni invoice ID</FormCapsLabel>
              <UnderlineInput
                value={amendInvoiceIdInput}
                onChange={(event) => setAmendInvoiceIdInput(event.target.value)}
                placeholder="e.g. INV-98765"
              />
            </div>
          ) : (
            <div className="rounded-md border border-slate-200 bg-white p-4 space-y-1">
              <FormCapsLabel>Omni invoice ID</FormCapsLabel>
              <p className="text-sm text-slate-800">{omniInvoiceId || '—'}</p>
            </div>
          )}
        </>
      ) : lockedStatus ? (
        <LockedStatusCard
          resourceLabel={resourceLabel}
          status={lockedStatus}
          actionLabel={lockedActionLabel ?? (lockedStatus === 'Invoiced' ? 'Move to Review' : 'Move to Booked')}
          actionTitle={lockedActionTitle}
          actionDescription={lockedActionDescription}
          lockedMessage={lockedMessage}
          reasonLabel={lockedReasonLabel}
          reasonPlaceholder={lockedReasonPlaceholder}
          confirmLabel={lockedConfirmLabel}
          cancelLabel={lockedCancelLabel}
          requireReason={Boolean(lockedActionTitle || lockedActionDescription || lockedReasonPlaceholder || lockedReasonLabel) || lockedStatus === 'Invoiced'}
          onAction={(reason) =>
            lockedStatus === 'Invoiced'
              ? onMoveToReview?.(reason || '')
              : onMoveToBooked?.()
          }
        />
      ) : null}

      <LinkedCustomer
        mode="read"
        customerName={customerName}
        customerId={customerId}
        details={{
          fullName: customerName,
          contactEmail,
          contactPhone,
          contactAddress: visitAddress,
          invoiceName: invoiceTo || customerName,
          invoiceEmail,
          invoicePhone,
          invoiceAddress,
          invoiceVat,
          externalId: omniCustomerId,
        }}
      />

      {serviceInvoice && customerInvoice ? (
        <InvoiceCompareOnPrep
          service={serviceInvoice}
          customer={customerInvoice}
          detailsLink={
            publicId
              ? { serviceType: detailsServiceType, id: publicId }
              : undefined
          }
        />
      ) : null}

      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-2">
        <FormCapsLabel>Contact</FormCapsLabel>
        <div className="space-y-2">
          <div className="space-y-1">
            <FormFieldLabel>Visit address</FormFieldLabel>
            <UnderlineTextarea
              value={visitAddress || customerAddress || '—'}
              readOnly
              rows={1}
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Site contact</FormFieldLabel>
            <UnderlineInput
              value={siteContact || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Phone</FormFieldLabel>
            <UnderlineInput
              value={contactPhone || contactCell || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Email</FormFieldLabel>
            <UnderlineInput
              value={contactEmail || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-2">
        <FormCapsLabel>Invoice details</FormCapsLabel>
        <div className="space-y-2">
          <div className="space-y-1">
            <FormFieldLabel>Invoice to</FormFieldLabel>
            <UnderlineInput
              value={invoiceTo || customerName || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Invoice email</FormFieldLabel>
            <UnderlineInput
              value={invoiceEmail || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Invoice phone</FormFieldLabel>
            <UnderlineInput
              value={invoicePhone || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Invoice address</FormFieldLabel>
            <UnderlineTextarea
              value={invoiceAddress || customerAddress || '—'}
              readOnly
              rows={1}
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>VAT</FormFieldLabel>
            <UnderlineInput
              value={invoiceVat || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <FormFieldLabel>Omni customer ID</FormFieldLabel>
            <UnderlineInput
              value={omniCustomerId || '—'}
              readOnly
              disableAutoSelect
              className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Notes from {resourceLabel}</h2>
        <p className="text-xs text-slate-500">
          Use these notes captured on the {resourceLabel} while you build the
          invoice in Omni. They can help explain decisions, work done, and
          any follow-up context, including photos.
        </p>

        {hasNotes ? (
          <div className="space-y-3">
            <div className="space-y-1 text-xs">
              <FormCapsLabel>Handled checklist</FormCapsLabel>
              <p className="text-[11px] text-slate-500">
                Tap anywhere on a row to mark a note as handled once
                it&apos;s been reflected on the invoice or the related
                action has been taken.
              </p>
            </div>
            <div className="mt-2 space-y-2">
              {nonStatusNotes.map((note) => {
                const isChecked = checkedNotes[note.id] ?? false

                return (
                  <button
                    type="button"
                    key={note.id}
                    className="flex w-full items-stretch gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 cursor-pointer"
                    onClick={(event) => {
                      const target = event.target as HTMLElement
                      if (target.closest('[data-zoom-image]')) {
                        return
                      }
                      toggleNoteHandled(note.id)
                    }}
                  >
                    <div className="flex-1">
                      {note.kind === 'photo' ? (
                        <CalloutPhotoNote
                          authorName={note.authorName}
                          createdAt={note.createdAt}
                          photoUrl={note.photoUrl}
                          photoUrls={note.photoUrls}
                          caption={note.caption}
                          mode="read"
                          enablePreview
                          showEditButton={false}
                          showDeleteButton={false}
                        />
                      ) : (
                        <CalloutNote
                          authorName={note.authorName}
                          createdAt={note.createdAt}
                          text={note.text}
                          mode="read"
                          showEditButton={false}
                          showDeleteButton={false}
                        />
                      )}
                    </div>
                    <div className="flex items-center pl-2">
                      <input
                        type="checkbox"
                        className="h-6 w-6 rounded border-2 border-slate-500 text-slate-900 accent-slate-900"
                        checked={isChecked}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          event.stopPropagation()
                          toggleNoteHandled(note.id)
                        }}
                        aria-label="Mark note as handled"
                      />
                    </div>
                  </button>
                )
              })}

              <InvoiceStatusChangesGroup
                statusChanges={groupedStatusChanges}
                checked={statusChangesHandled}
                onCheckedChange={setStatusChangesHandled}
              />
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No notes captured for this service.</p>
        )}
      </div>

      {lockedStatus ? (
        <LockedStatusCard
          resourceLabel={resourceLabel}
          status={lockedStatus}
          actionLabel={lockedActionLabel ?? (lockedStatus === 'Invoiced' ? 'Move to Review' : 'Move to Booked')}
          lockedMessage={lockedMessage}
          requireReason={lockedStatus === 'Invoiced'}
          onAction={(reason) =>
            lockedStatus === 'Invoiced'
              ? onMoveToReview?.(reason || '')
              : onMoveToBooked?.()
          }
        />
      ) : (
        <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
          <div className="space-y-1 text-xs">
            <FormCapsLabel>Invoice</FormCapsLabel>
            <p className="text-xs text-slate-500">
              {isJob
                ? `Capture the final billing outcome for this ${resourceLabel}. Record the Omni invoice ID when one exists, or mark it as a POS payment / no-Omni outcome.`
                : `Once the invoice is created in Omni, copy its ID here and mark the ${resourceLabel} as Invoiced. If no Omni invoice is created, you can explicitly record that below.`}
            </p>
          </div>

          <div className="mt-2 space-y-3 text-xs">
            {isJob ? (
              <div className="space-y-2">
                <FormFieldLabel>Billing outcome</FormFieldLabel>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="radio"
                      name="job-billing-outcome"
                      className="h-4 w-4 border-slate-300 text-slate-900 accent-slate-900"
                      checked={selectedBillingOutcome === 'OmniInvoice'}
                      onChange={() => setSelectedBillingOutcome('OmniInvoice')}
                    />
                    <span>Omni invoice created</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="radio"
                      name="job-billing-outcome"
                      className="h-4 w-4 border-slate-300 text-slate-900 accent-slate-900"
                      checked={selectedBillingOutcome === 'NoOmniInvoice'}
                      onChange={() => setSelectedBillingOutcome('NoOmniInvoice')}
                    />
                    <span>No Omni invoice created</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="radio"
                      name="job-billing-outcome"
                      className="h-4 w-4 border-slate-300 text-slate-900 accent-slate-900"
                      checked={selectedBillingOutcome === 'PosPayment'}
                      onChange={() => setSelectedBillingOutcome('PosPayment')}
                    />
                    <span>POS payment taken</span>
                  </label>
                </div>
              </div>
            ) : null}

            <div className="flex items-end gap-3">
              <FormFieldLabel className="w-36">Omni invoice ID</FormFieldLabel>
              <UnderlineInput
                value={omniInvoiceIdInput}
                onChange={(event) => setOmniInvoiceIdInput(event.target.value)}
                placeholder="e.g. INV-98765"
                disabled={isPosPayment}
              />
            </div>

            {isJob ? null : (
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 accent-slate-900"
                  checked={noOmniInvoice}
                  onChange={(event) =>
                    setSelectedBillingOutcome(
                      event.target.checked ? 'NoOmniInvoice' : 'OmniInvoice',
                    )
                  }
                />
                <span>No Omni invoice created for this {resourceLabel}</span>
              </label>
            )}

            <span
              className="inline-flex"
              title={isMarkingInvoiced ? 'Marking as invoiced…' : markInvoicedDisabledReason ?? undefined}
            >
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canMarkInvoiced || isMarkingInvoiced}
                onClick={handleMarkInvoicedClick}
              >
                {isMarkingInvoiced ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : null}
                {isMarkingInvoiced ? 'Marking…' : 'Mark as invoiced'}
              </button>
            </span>
            <p className="text-[11px] text-slate-500">
              {isJob
                ? `You can mark the ${resourceLabel} as invoiced for any billing outcome here. An Omni invoice ID is only required when "Omni invoice created" is selected.`
                : `You can only mark the ${resourceLabel} as invoiced after you&apos;ve checked all notes as handled, and then captured the final billing outcome for this ${resourceLabel}.`}
            </p>
          </div>
        </div>
      )}
    </section>
  )

  if (!showHeader) return body

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Invoice Service</h1>
          <p className="text-sm text-slate-600">
            Use this page alongside Omni to build an invoice for the service.
          </p>
        </header>
        {body}
      </main>
    </div>
  )
}

export const demoInvoiceProps: InvoiceServiceScreenProps = {
  serviceType: 'Callout',
  customerName: 'John Doe',
  visitAddress: '123 Main Road, Business Park',
  siteContact: 'Jane Smith',
  contactEmail: 'jane.smith@example.com',
  contactPhone: '+1 555 123 4567',
  invoiceTo: 'John Doe',
  invoiceEmail: 'accounts@example.com',
  invoicePhone: '+1 555 123 4567',
  invoiceAddress: '123 Main Road, Business Park',
  invoiceVat: 'VAT123456',
  issueSummary: 'Investigate power outage in office block',
  calloutStatus: 'ReadyToInvoice',
  omniCustomerId: 'CUST-12345',
  notes: [],
}
